// Permission management system for OU-based access control

export const PERMISSIONS = {
  USER_MANAGEMENT: 'user_management',
  ROLE_ASSIGNMENT: 'role_assignment',
  OU_MANAGEMENT: 'ou_management',
  POLICY_MANAGEMENT: 'policy_management',
  VIEW_USERS: 'view_users',
  VIEW_OUS: 'view_ous',
  VIEW_ROLES: 'view_roles'
};

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  OU_ADMIN: 'ou_admin',
  USER_ADMIN: 'user_admin',
  VIEWER: 'viewer'
};

// Default role permissions
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: [
    PERMISSIONS.USER_MANAGEMENT,
    PERMISSIONS.ROLE_ASSIGNMENT,
    PERMISSIONS.OU_MANAGEMENT,
    PERMISSIONS.POLICY_MANAGEMENT,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_OUS,
    PERMISSIONS.VIEW_ROLES
  ],
  [ROLES.OU_ADMIN]: [
    PERMISSIONS.USER_MANAGEMENT,
    PERMISSIONS.ROLE_ASSIGNMENT,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_OUS,
    PERMISSIONS.VIEW_ROLES
  ],
  [ROLES.USER_ADMIN]: [
    PERMISSIONS.USER_MANAGEMENT,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_OUS
  ],
  [ROLES.VIEWER]: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_OUS,
    PERMISSIONS.VIEW_ROLES
  ]
};

// Check if user has permission for specific action
export const hasPermission = (userPermissions, permission) => {
  return userPermissions.includes(permission) || userPermissions.includes('*');
};

// Check if user can access specific OU
export const canAccessOU = (userOUs, targetOU) => {
  // Super admin can access all OUs
  if (userOUs.includes('*')) return true;
  
  // Check if user has access to the specific OU or any parent OU
  return userOUs.some(userOU => {
    // Exact match
    if (userOU === targetOU) return true;
    
    // Check if target OU is a child of user's OU
    if (targetOU.startsWith(userOU + '/')) return true;
    
    return false;
  });
};

// Get user's accessible OUs
export const getAccessibleOUs = (userOUs, allOUs) => {
  if (userOUs.includes('*')) return allOUs;
  
  return allOUs.filter(ou => canAccessOU(userOUs, ou.id));
};

// Filter data based on user permissions
export const filterDataByPermissions = (data, userPermissions, userOUs) => {
  if (!data) return [];
  
  return data.filter(item => {
    // Check if user can access the OU this item belongs to
    const itemOU = item.organisationalUnit || item.orgUnitPath || item.id || item.parentId;
    if (itemOU && !canAccessOU(userOUs, itemOU)) {
      return false;
    }
    return true;
  });
};

// Get user's effective permissions
export const getUserPermissions = (userRole, delegatedPermissions = []) => {
  const basePermissions = ROLE_PERMISSIONS[userRole] || [];
  return [...new Set([...basePermissions, ...delegatedPermissions])];
};

// Validate user can perform action on specific OU
export const validateAction = (action, targetOU, userPermissions, userOUs) => {
  console.log('validateAction called:', { action, targetOU, userPermissions, userOUs });
  
  // Super admin can do everything
  if (userPermissions.includes('*') || userOUs.includes('*')) {
    console.log('Super admin access granted');
    return { allowed: true };
  }
  
  // Check if user has the required permission
  if (!hasPermission(userPermissions, action)) {
    console.log('Permission check failed:', { action, userPermissions });
    return { allowed: false, reason: 'Insufficient permissions' };
  }
  
  // Check if user can access the target OU
  if (targetOU && !canAccessOU(userOUs, targetOU)) {
    console.log('OU access check failed:', { targetOU, userOUs });
    return { allowed: false, reason: 'Cannot access this organisational unit' };
  }
  
  console.log('Permission validation passed');
  return { allowed: true };
}; 